// Module 12 §6 — the feature's ONLY HTTP boundary (contract 03 §15). Nothing else in
// `features/integrations` imports `appHttp`; in v3 these four requests lived inside the Pinia
// store itself (`integrationStore.ts:232, 248, 294, 317`), three of them swallowing failures into
// `console.error` and one with no `catch` at all.
//
// TWO ENVELOPE SHAPES COEXIST and both are preserved — this is wire fact, not inconsistency:
//   • `get-allowed-integrations` and `get-connectable-integrations` return `{ status, data: {...} }`
//     → `unwrapEnvelope`
//   • `get-all-integration` returns the array FLAT → `unwrap`
//
// The fourth v3 request, `get_tiktok_accounts` (`:294`), is deliberately NOT here. It feeds the
// TikTok connector modal's account picker, not any page-level read, and contract §6's registry/list
// table names exactly the three below. It lands with the TikTok connector port.
import type { AxiosRequestConfig } from 'axios'

import type {
  AllowedIntegrationsDto,
  ConnectableIntegrationsDto,
  WorkspaceIntegrationDetailDto,
  WorkspaceIntegrationsDto,
} from '../models/integration.types'

import { appHttp, unwrap, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

/**
 * Failures route to `integrationService`, not the global toast.
 *
 * These three run on every boot and every workspace switch. A transient 500 must degrade the
 * Connections page, not raise a toast on whatever screen the user happens to be looking at — v3
 * reached the same outcome by accident, via the bare `console.error` catches.
 */
const FEATURE_OWNED_ERRORS: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

export class IntegrationApi {
  /**
   * Every workspace's linked accounts for one account.
   *
   * `accountId` is the WORKSPACE's `account_id` when the caller is a workspace admin, else the
   * selected account id — the branch lives in `integrationService`, which is where 07's surface is
   * in scope.
   */
  static async getAllIntegrations(accountId: number | string): Promise<WorkspaceIntegrationsDto[]> {
    const response = await appHttp.get<WorkspaceIntegrationsDto[]>(
      `/integrations/${accountId}/get-all-integration/`,
      FEATURE_OWNED_ERRORS,
    )
    return unwrap(response) ?? []
  }

  /** What this workspace's package allows, plus the parked `integration_groups` (BE-12-08). */
  static async getAllowedIntegrations(
    workspaceId: number | string,
  ): Promise<AllowedIntegrationsDto> {
    const response = await appHttp.get<ApiEnvelope<AllowedIntegrationsDto>>(
      `/integrations/${workspaceId}/get-allowed-integrations/`,
      FEATURE_OWNED_ERRORS,
    )
    const data = unwrapEnvelope(response)
    return {
      allowed_integrations: data?.allowed_integrations ?? [],
      integration_groups: data?.integration_groups ?? null,
    }
  }

  /**
   * Which integrations this user may connect for this account.
   *
   * The `?is_workspace_admin=true` query is only appended for a workspace admin — v3 omits the
   * param entirely otherwise (`integrationStore.ts:315`) rather than sending `false`.
   */
  static async getConnectableIntegrations(
    accountId: number | string,
    userId: number | string,
    isWorkspaceAdmin: boolean,
  ): Promise<ConnectableIntegrationsDto> {
    const path = `/integrations/${accountId}/${userId}/get-connectable-integrations/`
    const response = await appHttp.get<ApiEnvelope<ConnectableIntegrationsDto>>(
      isWorkspaceAdmin ? `${path}?is_workspace_admin=true` : path,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response) ?? {}
  }

  /**
   * One workspace's integration detail — the account-wide connection counter's only source.
   *
   * A FOURTH page-level read, contrary to this file's own "three" header, and deliberately so:
   * `paid_media_connections` rides on this endpoint alone (`integrations/views.py:321`), the
   * bootstrap carries no section for it, and the MCP Ad accounts screen has to print the number
   * the backend will enforce on the next connect. It is not added to `refreshAll` — every boot and
   * every workspace switch would then pay for a counter three of the four callers never render.
   */
  static async getWorkspaceDetail(
    workspaceId: number | string,
  ): Promise<WorkspaceIntegrationDetailDto | null> {
    const response = await appHttp.get<WorkspaceIntegrationDetailDto>(
      `/integrations/${workspaceId}/`,
      FEATURE_OWNED_ERRORS,
    )
    return unwrap(response) ?? null
  }

  /**
   * Disconnect ad accounts by their `WorkspaceIntegrationAccount` ids.
   *
   * The backend takes EITHER `account_ids` or `integration_types`, never both, and refuses a body
   * carrying neither (`integrations/contracts.py:43-68`). Only the by-account selector is exposed
   * here: the by-type selector disconnects every account of a platform at once, which no screen
   * asks for and which no confirmation this feature shows would be describing.
   */
  static async deleteAdAccounts(
    workspaceId: number | string,
    accountIds: readonly (number | string)[],
  ): Promise<void> {
    await appHttp.post(
      `/integrations/${workspaceId}/delete-integration/`,
      { account_ids: accountIds.map(Number) },
      FEATURE_OWNED_ERRORS,
    )
  }
}
